import type {
	CoursesAdminCreateTagInviteDto,
	CoursesAdminSetTagInviteEnabledDto,
	CoursesTagInviteTokenDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	GetTagInviteInput,
	GetTagInviteOutput,
	RedeemTagInviteInput,
	RedeemTagInviteOutput,
	AdminListTagInvitesOutput,
	AdminListTagInviteRegistrantsInput,
	AdminListTagInviteRegistrantsOutput,
	AdminCreateTagInviteInput,
	AdminCreateTagInviteOutput,
	AdminSetTagInviteEnabledInput,
	AdminSetTagInviteEnabledOutput,
	AdminDeleteTagInviteInput,
	AdminDeleteTagInviteOutput,
} from '../../types/tag-invites.types';

const TAG = 'courses:tag-invites' as const;
const INVALIDATES = [TAG, 'courses:courses', 'courses:dashboard'] as const;

export const tagInvitesEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		getTagInvite: builder.query<GetTagInviteOutput, GetTagInviteInput>({
			query: (body) => ({
				url: 'courses/public/tag-invite',
				method: 'POST',
				body: body satisfies Wire<CoursesTagInviteTokenDto>,
			}),
			providesTags: [TAG],
			extraOptions: { skipAuth: true },
		}),
		redeemTagInvite: builder.mutation<RedeemTagInviteOutput, RedeemTagInviteInput>({
			query: (body) => ({
				url: 'courses/tags/invites/redeem',
				method: 'POST',
				body: body satisfies Wire<CoursesTagInviteTokenDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListTagInvites: builder.query<AdminListTagInvitesOutput, void>({
			query: () => ({ url: 'courses/admin/platform/tag-invites' }),
			providesTags: [TAG],
		}),
		adminListTagInviteRegistrants: builder.query<
			AdminListTagInviteRegistrantsOutput,
			AdminListTagInviteRegistrantsInput
		>({
			query: ({ inviteId }) => ({
				url: `courses/admin/platform/tag-invites/${encodeURIComponent(inviteId)}/registrants`,
			}),
			providesTags: [TAG],
		}),
		adminCreateTagInvite: builder.mutation<AdminCreateTagInviteOutput, AdminCreateTagInviteInput>({
			query: (body) => ({
				url: 'courses/admin/platform/tag-invites',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminCreateTagInviteDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminSetTagInviteEnabled: builder.mutation<
			AdminSetTagInviteEnabledOutput,
			AdminSetTagInviteEnabledInput
		>({
			query: ({ id, ...body }) => ({
				url: `courses/admin/platform/tag-invites/${encodeURIComponent(id)}/enabled`,
				method: 'PATCH',
				body: body satisfies Wire<CoursesAdminSetTagInviteEnabledDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminDeleteTagInvite: builder.mutation<AdminDeleteTagInviteOutput, AdminDeleteTagInviteInput>({
			query: ({ id }) => ({
				url: `courses/admin/platform/tag-invites/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
	}),
});

export const {
	useGetTagInviteQuery,
	useLazyGetTagInviteQuery,
	useRedeemTagInviteMutation,
	useAdminListTagInvitesQuery,
	useLazyAdminListTagInvitesQuery,
	useAdminListTagInviteRegistrantsQuery,
	useLazyAdminListTagInviteRegistrantsQuery,
	useAdminCreateTagInviteMutation,
	useAdminSetTagInviteEnabledMutation,
	useAdminDeleteTagInviteMutation,
} = tagInvitesEndpoints;
